import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPasswordSchema, type ResetPasswordInput } from '@mygmao/shared';
import { Lock, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { api, extractErrorMessage } from '../lib/api';

/**
 * Page /reset-password?token=… (US-AUTH-03).
 *
 * - Lit le token depuis la query string.
 * - Demande le nouveau mdp + confirmation.
 * - POST /auth/reset-password → succès → redirige vers /login.
 * - Token invalide / expiré / déjà utilisé → message d'erreur.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [status, setStatus] = useState<'idle' | 'done' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { token },
  });

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 max-w-md w-full p-8 text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Lien invalide</h1>
          <p className="text-sm text-gray-600 mb-4">
            Ce lien de réinitialisation est incomplet. Demande un nouveau lien.
          </p>
          <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">
            Demander un nouveau lien
          </Link>
        </div>
      </div>
    );
  }

  async function onSubmit(values: ResetPasswordInput) {
    setErrorMessage(null);
    try {
      await api.post('/auth/reset-password', values);
      setStatus('done');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      setStatus('error');
      setErrorMessage(extractErrorMessage(err, 'Réinitialisation impossible'));
    }
  }

  if (status === 'done') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 max-w-md w-full p-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Mot de passe mis à jour</h1>
          <p className="text-sm text-gray-600">Redirection vers la page de connexion…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="bg-white rounded-xl shadow-sm border border-gray-200 max-w-md w-full p-8"
      >
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
            <Lock className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Nouveau mot de passe</h1>
            <p className="text-xs text-gray-500">Choisis un mot de passe sécurisé</p>
          </div>
        </div>

        {status === 'error' && errorMessage && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        <input type="hidden" {...register('token')} />

        <div className="mb-3">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            Nouveau mot de passe <span className="text-red-500">*</span>
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            {...register('password')}
          />
          {errors.password ? (
            <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
          ) : (
            <p className="text-gray-400 text-xs mt-1">
              8+ caractères, majuscule, minuscule, chiffre, caractère spécial
            </p>
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700 mb-2">
            Confirmer le mot de passe <span className="text-red-500">*</span>
          </label>
          <input
            id="passwordConfirm"
            type="password"
            autoComplete="new-password"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            {...register('passwordConfirm')}
          />
          {errors.passwordConfirm && (
            <p className="text-red-500 text-xs mt-1">{errors.passwordConfirm.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Mise à jour…
            </>
          ) : (
            'Réinitialiser le mot de passe'
          )}
        </button>
      </form>
    </div>
  );
}
